/*
 * CSL BRANDS Mentoring Program — English copy.
 *
 * Mirrors content/fr.js exactly, key for key, so both locales stay in sync.
 */

import { PROJECT_LINKS } from '../../../lib/projectLinks';

const shared = {
  marquee: [
    "Applications open",
    "Douala // Cameroon",
    "10 seats",
    "6 months",
    "1:1 every 2 weeks",
  ],
  cohortFacts: [
    { label: "Start", value: "January 2027" },
    { label: "Location", value: "Douala, Cameroon" },
    { label: "Duration", value: "6 months" },
    { label: "Seats", value: "10 seats" },
  ],
  cta: {
    kicker: "Next cohort — January 2027",
    title: "Ten seats. Six months. Your move",
    text: "Mentoring starts with a conversation. Tell us where you are and what you want to build — we respond to every message.",
    primary: { label: "Apply to Cohort 01", target: "contact" },
    secondary: { label: "Talk to the team", target: "contact" },
  },
  footer: {
    description:
      "The CSL BRANDS Mentoring Program is a six-month track in Douala, and the operating foundation of a wider home for talent, founders, and technology ventures in Cameroon.",
    siteTitle: "Site",
    reachTitle: "Reach us",
    bottomLeft: "© 2026 CSL BRANDS. Mentoring Program.",
    legal: [
      { label: "Privacy", target: "hostPrivacy" },
      { label: "Terms", target: "hostTerms" },
      { label: "Sitemap", target: "hostSitemap" },
    ],
    social: [
      { label: "LinkedIn", href: "https://www.linkedin.com/company/kursa-brands/" },
      { label: "Facebook", href: "https://web.facebook.com/getkursa/" },
    ],
  },
};

const projects = [
  {
    key: "kursa",
    label: "CSL BRANDS / Products / KURSA",
    tag: "CSL BRANDS product",
    title: "KURSA",
    text: "A learning platform for launching and selling online courses.",
    href: PROJECT_LINKS.kursa,
  },
  {
    key: "primecorn",
    label: "CSL BRANDS / Products / PrimeCorn",
    tag: "CSL BRANDS product",
    title: "PrimeCorn",
    text: "ERP and CRM tools for managing business operations in one place.",
    href: PROJECT_LINKS.primecorn,
  },
  {
    key: "tchopnjoh",
    label: "CSL BRANDS / Products / TchopNjoh",
    tag: "CSL BRANDS product",
    title: "TchopNjoh",
    text: "Restaurant software for ordering, menus, stock, and customer loyalty.",
    href: PROJECT_LINKS.tchopnjoh,
  },
];

export const en = {
  locale: "en",
  seo: {
    index: {
      title: "CSL BRANDS Mentoring Program — Douala, Cameroon",
      description:
        "Six months of 1:1 mentoring in Douala: structured training, team projects, and sprint delivery, guided by working engineers.",
    },
    program: {
      title: "The program — CSL BRANDS Mentoring Program",
      description:
        "The full program: who it is for, the learning model, curriculum, 1:1 mentoring, selection, commitment, and what comes next.",
    },
    projects: {
      title: "Projects and talent — CSL BRANDS Mentoring Program",
      description:
        "Explore CSL BRANDS products and visit the same project links featured on our main site.",
    },
    about: {
      title: "About — CSL BRANDS Mentoring Program",
      description:
        "Where we work, how we operate, the CSL BRANDS ecosystem, our history, and the vision for a wider space in Douala.",
    },
  },
  nav: {
    skipToContent: "Skip to content",
    menu: "Menu",
    close: "Close",
    brand: "Mentoring Program",
    // Short form for the host site's top navbar — see the fr note.
    navLabel: "Mentoring",
    brandName: "CSL BRANDS",
    links: [
      { pageKey: "index", label: "Home" },
      { pageKey: "program", label: "Program" },
      { pageKey: "projects", label: "Projects" },
      { pageKey: "about", label: "About" },
    ],
    home: "Back to the CSL BRANDS site",
    languageLabel: "Language",
    languageFr: "Français",
    languageEn: "English",
    openCta: "Apply",
  },
  shared,
  index: {
    hero: {
      ticker: ["Pipeline // Cohort 01", "Douala // Cameroon", "Mentoring // 1:1"],
      eyebrow: "Mentoring Program · CSL BRANDS",
      titleLines: ["Training alone.", "Shipping in a team."],
      lead: "The CSL BRANDS Mentoring Program is a six-month track in Douala where emerging engineers are paired with a working engineer in structured 1:1 sessions every two weeks — and where companies hire juniors who already know how to work in a team.",
      primary: { label: "Apply to Cohort 01", target: "contact" },
      secondary: { label: "How the program works", target: "program" },
      facts: shared.cohortFacts,
    },
    pipeline: {
      label: "The pipeline, step by step",
      steps: [
        { number: "01", title: "Apply", text: "Rolling apply" },
        { number: "02", title: "Selection", text: "2 weeks" },
        { number: "03", title: "Piscine", text: "2 weeks" },
        { number: "04", title: "Program", text: "4 months" },
        { number: "05", title: "Team project", text: "2 months" },
        { number: "06", title: "Internship", text: "6 months" },
      ],
    },
    stats: [
      { value: "10", unit: "Seats" },
      { value: "2", unit: "Wk Piscine" },
      { value: "4", unit: "Mo mentoring" },
      { value: "2", unit: "Mo project" },
    ],
    gap: {
      marker: "01",
      eyebrow: "The gap",
      title: "Finishing a course is not the same as being ready for a team.",
      lead: "Many talented learners know the tools but have never shipped inside a real workflow — system thinking, code review, sprint discipline, product decisions, work that others must use and maintain.",
      cards: [
        { key: "A", title: "Courses alone", text: "Syntax, small apps, solo work. Knowledge without team context." },
        { key: "B", title: "The missing layer", text: "Reviews, sprints, contracts, trade-offs, communication, delivery." },
        { key: "C", title: "Team-ready", text: "Contributing to real systems with discipline others can rely on." },
      ],
    },
    journey: {
      marker: "02",
      eyebrow: "The journey",
      title: "Six months. Real workflows. Small cohort.",
      lead: "Four months of mentored, structured training, then two months shipping a team project — the conditions of a real engineering team, before you join one.",
      link: { label: "Explore the program", target: "program" },
      steps: [
        { number: "01", title: "Discover & select", text: "We look for curiosity, discipline, foundational ability, and the willingness to learn by doing. Small cohorts, deliberate selection." },
        { number: "02", title: "Prepare", text: "Selected applicants complete assigned foundational learning and set up the tools and habits the cohort runs on." },
        { number: "03", title: "Build foundations", text: "Core engineering, product, and collaboration concepts — from version control to system structure." },
        { number: "04", title: "Work in teams", text: "Plan solutions, write user stories, define domains and APIs, work in sprints, review code, respond to feedback." },
        { number: "05", title: "Ship projects", text: "Build usable solutions to locally relevant problems. Show the work, the process, and the learning." },
        { number: "06", title: "Transition", text: "Move toward an internship, CSL BRANDS work, external employment, further specialization, or a founder path." },
      ],
    },
    proof: {
      marker: "03",
      eyebrow: "What we build",
      title: "Explore products from CSL BRANDS.",
      link: { label: "All projects", target: "projects" },
      projects,
    },
    mentoring: {
      marker: "04",
      eyebrow: "What mentoring adds",
      title: "Juniors who arrive already working like a team.",
      lead: "Every mentee completes four months of training with an assigned mentor, then two months of team-based product development on a real project. By the end they have worked in collaborative engineering environments, built production-minded software through Git and Agile workflows, and developed the habits a modern team expects.",
      items: [
        { number: "01", title: "Git discipline", text: "Branches, pull requests, and code review as a daily habit — not theory." },
        { number: "02", title: "Agile delivery", text: "User stories, sprints, stand-ups, and demos across six months." },
        { number: "03", title: "Team collaboration", text: "Four months mentored, two months shipping together on one product." },
        { number: "04", title: "A mentor, not an answer key", text: "A working engineer who reviews your work and pushes back on your decisions." },
        { number: "05", title: "Evidence, not claims", text: "Review the projects, the repos, and how each team actually worked." },
      ],
    },
    vision: {
      kicker: "We are building",
      title: "A home for talent, founders, and technology ventures in Cameroon.",
      lead: "The mentoring program is the operating foundation. CSL BRANDS runs the training next to real product work, in dedicated space in Douala — training rooms, work areas, and events. We announce facilities when they open, not before.",
      link: { label: "The vision", target: "about" },
    },
    waysIn: {
      marker: "05",
      eyebrow: "Find your way in",
      title: "Four ways to join the program.",
      cards: [
        { key: "mentee", tag: "For mentees", title: "Apply", text: "Apply to Cohort 01 — starts January 2027.", cta: "Apply", target: "contact" },
        { key: "mentor", tag: "For mentors", title: "Become a mentor", text: "Guide a mentee in 1:1 sessions every two weeks.", cta: "Become a mentor", target: "contact" },
        { key: "employer", tag: "For employers", title: "Meet talent", text: "Review real work samples and meet team-ready juniors.", cta: "Meet talent", target: "contact" },
        { key: "sponsor", tag: "For sponsors", title: "Sponsor a cohort", text: "Back ten careers, documented publicly.", cta: "Sponsor a cohort", target: "contact" },
      ],
    },
  },
  program: {
    hero: {
      ticker: ["Mentoring // 1:1 every 2 weeks", "Douala // Cameroon", "6 months"],
      eyebrow: "The program",
      titleLines: ["The mentoring program."],
      lead: "Six months inside the conditions of a real engineering team: structured coursework, bi-weekly 1:1 mentorship, team projects, and sprint delivery — building work you can show, and habits a team can rely on.",
      primary: { label: "Apply to Cohort 01", target: "contact" },
      secondary: { label: "Ask a question", target: "contact" },
      facts: shared.cohortFacts,
    },
    audience: {
      marker: "01",
      eyebrow: "Who it's for",
      title: "For engineers at the start of the climb.",
      lead: "The program is built for people who already have foundations and want the team layer on top: workflows, reviews, delivery, and judgment.",
      profilesTitle: "Profiles",
      profiles: [
        { title: "Graduates", text: "Engineering graduates from higher institutions and universities." },
        { title: "Final-year", text: "Final-year engineering students completing their internship period." },
        { title: "Self-taught", text: "Self-taught learners and career switchers with equivalent foundations — make your case in the application essay." },
      ],
      needsTitle: "What you need",
      needs: [
        { title: "Personal laptop", text: "Your own machine, set up as your daily workshop." },
        { title: "GitHub account", text: "Your work lives in version control from day one." },
        { title: "Based in Douala", text: "In-person attendance, daily, for the full program." },
        { title: "Real commitment", text: "Coursework, team projects, sprints — six months of showing up." },
      ],
    },
    model: {
      marker: "02",
      eyebrow: "The learning model",
      title: "Six steps, from selection to transition.",
      lead: "Each step raises the standard: from foundations, to teamwork, to shipping work others can use.",
      steps: [
        { number: "01", title: "Discover & select", text: "We look for curiosity, discipline, foundational ability, and the willingness to learn by doing. Small cohorts, deliberate selection." },
        { number: "02", title: "Prepare", text: "Selected applicants complete assigned foundational learning and set up the tools and habits the cohort runs on." },
        { number: "03", title: "Build foundations", text: "Core engineering, product, and collaboration concepts — from version control to system structure." },
        { number: "04", title: "Work in teams", text: "Plan solutions, write user stories, define domains and APIs, work in sprints, review code, respond to feedback." },
        { number: "05", title: "Ship projects", text: "Build usable solutions to locally relevant problems. Show the work, the process, and the learning." },
        { number: "06", title: "Transition", text: "Move toward an internship, CSL BRANDS work, external employment, further specialization, or a founder path." },
      ],
    },
    curriculum: {
      marker: "03",
      eyebrow: "Curriculum",
      title: "Capabilities, not a tool list.",
      lead: "Everything below is used together inside real team workflows — on projects, under review, in sprints. Nothing is taught as an isolated course and left on the shelf.",
      groups: [
        { title: "Foundations", items: ["Linux & dev environment", "Git & GitHub", "HTML & CSS", "JavaScript", "NPM"] },
        { title: "Engineering", items: ["Go", "React", "Next.js", "Relational databases", "API design", "Auth & authorization"] },
        { title: "Systems", items: ["Design patterns", "Domain-Driven Design", "Domain boundaries", "API contracts", "Maintainability trade-offs"] },
        { title: "Delivery", items: ["Agile & Scrum", "User stories & sprints", "Linting & testing", "CI/CD with GitHub Actions", "Pull requests & review", "Refactoring"] },
        { title: "Professional", items: ["Product ideation", "UX exposure", "Communication", "Teamwork", "Book Club", "Public demos"] },
      ],
    },
    mentorship: {
      marker: "04",
      eyebrow: "Mentorship",
      title: "A working engineer in your corner.",
      lead: "Every mentee is matched with a mentor for the full program — structured sessions every two weeks to review work, question decisions, and pressure-test thinking against real-world experience.",
      facts: [
        { label: "Cadence", value: "1:1 sessions every two weeks" },
        { label: "Who mentors", value: "Working engineers and practitioners" },
        { label: "What happens", value: "Work review, real-world context, guidance" },
      ],
      pillars: [
        { key: "A", title: "Review the work", text: "Code, decisions, and trade-offs get looked at by someone who ships professionally." },
        { key: "B", title: "Add real context", text: "How teams actually plan, disagree, recover, and deliver — beyond what coursework covers." },
        { key: "C", title: "Guide the path", text: "Career direction, specialization, and honest feedback on readiness." },
      ],
      expertiseTitle: "The areas our mentors cover",
      expertiseLead: "We match pairs against each mentee's project and goals. These are the expertises drawn on across previous cohorts.",
      expertise: [
        { title: "Backend & distributed systems", text: "Go, architectures that hold under load, API contracts, and migrations without downtime." },
        { title: "Frontend & design systems", text: "React, Next.js, accessibility, and components a whole team can evolve." },
        { title: "Data & product", text: "Modelling, data pipelines, and turning an idea into a correctly framed problem." },
        { title: "DevOps & platform", text: "CI/CD, infrastructure as code, observability, and deploys that wake nobody at 3am." },
        { title: "QA & application security", text: "Testing, application security review, and the failures you only catch in production." },
        { title: "Career & architecture", text: "Architecture review, interview preparation, and trading stability against speed." },
      ],
      cta: { label: "Become a mentor", target: "contact" },
    },
    selection: {
      marker: "05",
      eyebrow: "Selection",
      title: "Deliberate, in person, competitive.",
      lead: "Small cohorts — 10 seats — mean every seat is earned. The process itself is the first test of commitment.",
      steps: [
        { number: "01", title: "Apply", text: "Submit your background, your motivation, and the essay." },
        { number: "02", title: "Selection", text: "Application review, assigned prep work, and an in-person interview." },
        { number: "03", title: "Piscine", text: "A two-week sink-or-swim challenge — a test of passion, not skill." },
        { number: "04", title: "Program", text: "Six months of structured coursework, mentorship, and team delivery." },
        { number: "05", title: "Internship", text: "Six months inside real work — the bridge into industry." },
      ],
    },
    commitment: {
      marker: "06",
      eyebrow: "Commitment",
      title: "Know what you are signing up for.",
      lead: "The program asks a lot on purpose. This is the deal, in plain terms.",
      facts: [
        { label: "Piscine", value: "2 weeks, sink or swim — before the program" },
        { label: "Duration", value: "6 months, structured" },
        { label: "Location", value: "Douala, Cameroon — in person, daily" },
        { label: "Cohort size", value: "~10 mentees, selective" },
        { label: "Mentorship", value: "1:1 sessions every two weeks" },
        { label: "Structure", value: "4 months mentored training + 2 months team project" },
        { label: "Equipment", value: "Personal laptop + GitHub account" },
        { label: "After the program", value: "6-month internship and industry transition" },
      ],
    },
    after: {
      marker: "07",
      eyebrow: "After the program",
      title: "A pathway, not a promise.",
      lead: "The six-month phase leads into internship and industry transition pathways. Where each mentee lands depends on their work, the market, and the opportunities we open together.",
      note: "We don't promise jobs. We build the evidence — real projects, real workflow discipline — that earns them.",
      steps: [
        { number: "01", title: "Internship", text: "A structured internship phase to apply the discipline in practice." },
        { number: "02", title: "CSL BRANDS", text: "Contributing to CSL BRANDS engineering projects where openings exist." },
        { number: "03", title: "External employment", text: "Introductions to employers who evaluate evidence, not certificates." },
        { number: "04", title: "Specialization", text: "Deeper study in the direction your project work pointed to." },
        { number: "05", title: "Founder path", text: "Building on your own idea, where the fit and support exist." },
      ],
    },
    faq: {
      marker: "08",
      eyebrow: "FAQ",
      title: "Asked, answered.",
      lead: "Something we missed? Ask us directly — we respond to every message.",
      link: { label: "Contact us", target: "contact" },
      items: [
        { question: "Who is eligible?", answer: "Engineering graduates, final-year engineering students on internship, and self-taught learners or career switchers with equivalent foundations. In the last case, your essay has to make the case." },
        { question: "How long is the program?", answer: "Six months — four months of mentored training, then two months of team project. Before that there is a two-week piscine and a preparation phase." },
        { question: "How are mentees selected?", answer: "Application review, assigned prep work, an in-person interview in Douala, then a two-week piscine. Every applicant gets a response." },
        { question: "What do I need?", answer: "A personal laptop, a GitHub account, and the ability to show up in Douala every day for six months." },
        { question: "What does it cost?", answer: "The program is free. We exist because companies and sponsors fund it, which is what keeps selection strict. If you would like to sponsor a cohort, get in touch." },
        { question: "Is it remote?", answer: "No. Daily in-person attendance in Douala is part of the commitment — it is what makes the mentoring and the team work real." },
      ],
    },
  },
  projects: {
    hero: {
      ticker: ["Projects & talent // CSL BRANDS", "Products // live links"],
      eyebrow: "Projects and talent",
      titleLines: ["Proof,", "not promises."],
      lead: "Explore the products CSL BRANDS builds. Each card links directly to the product featured on our main site.",
      figure: { label: "CSL BRANDS / Products" },
    },
    teamProjects: {
      marker: "01",
      eyebrow: "CSL BRANDS products",
      title: "Products you can explore now.",
      lead: "These are CSL BRANDS products from What We Build & Do. Follow each link to see the product itself.",
      items: projects,
    },
    footprint: {
      marker: "02",
      eyebrow: "Engineering footprint",
      title: "Explore the full product line.",
      lead: "The links below come from the CSL BRANDS product showcase.",
      tableTitle: "Products",
      columns: { repository: "Product", classification: "Type", language: "Visit" },
      rows: [
        { repository: "KURSA", classification: "Learning platform", language: "Visit product", href: PROJECT_LINKS.kursa },
        { repository: "Shopikat", classification: "E-commerce platform", language: "Visit product", href: PROJECT_LINKS.shopikat },
        { repository: "PrimeCorn", classification: "ERP / CRM", language: "Visit product", href: PROJECT_LINKS.primecorn },
        { repository: "TchopNjoh", classification: "Restaurant software", language: "Visit product", href: PROJECT_LINKS.tchopnjoh },
      ],
      link: { label: "What We Build & Do", href: "/#services" },
    },
    employers: {
      marker: "03",
      eyebrow: "For employers",
      kicker: "Hire on evidence",
      title: "Review how the team worked. Then meet them.",
      lead: "Visit the products above to see CSL BRANDS work. To discuss the mentoring program or meet future talent, contact our team.",
      cta: { label: "Meet talent", target: "contact" },
    },
  },
  about: {
    hero: {
      ticker: ["About", "Douala // Cameroon"],
      eyebrow: "About",
      titleLines: ["About"],
      lead: "The CSL BRANDS Mentoring Program exists because completing a course is not the same as contributing to a real engineering team.",
      leadExtra: "Mentoring closes that gap the only way it closes: through real work — projects, reviews, sprints, demos — in small cohorts, in Douala.",
    },
    where: {
      marker: "01",
      eyebrow: "Where we work",
      title: "Rooted in Douala. Built to a wider standard.",
      lead: "We work from Douala, in Cameroon's Littoral — home to a dense ecosystem of developers, designers, and founders that has grown around the city. Being local is the point: mentees build solutions to problems they can see. But the standard is not local — we train to the workflows of modern professional teams, so the work holds up in distributed and global settings.",
      facts: [
        { label: "Base", value: "Douala, Littoral, Cameroon" },
        { label: "Format", value: "In person, daily" },
        { label: "Ecosystem", value: "Littoral / Tech hub" },
      ],
      figure: { label: "Media / About / Douala" },
    },
    operation: {
      marker: "02",
      eyebrow: "How we operate",
      title: "Run like an engineering team, because it is one.",
      items: [
        { number: "01", title: "Small cohorts", text: "Around ten seats, selected deliberately. Everyone is known, everyone is accountable." },
        { number: "02", title: "Real projects", text: "Work moves through user stories, sprints, and demos — not exercises with answer keys." },
        { number: "03", title: "Review culture", text: "Code, writing, and decisions all pass through review. Feedback is the default, not an event." },
        { number: "04", title: "Communication as engineering", text: "Stand-ups, written updates, and public demos are treated as core discipline, not soft extras." },
        { number: "05", title: "Documented publicly", text: "Cohort progress is published as it happens. The record is the proof." },
      ],
      rhythmTitle: "The rhythm",
      rhythmLead: "Around the coursework and sprints, a documented community cadence keeps the work public and the pressure honest:",
      rhythm: ["In-house hackathons", "Public demos", "The Book Club", "Community meetups"],
    },
    ecosystem: {
      marker: "03",
      eyebrow: "The CSL BRANDS ecosystem",
      title: "One name, three functions.",
      lead: "The CSL BRANDS Mentoring Program is fully owned and operated by CSL BRANDS. That matters practically: the training runs next to real product work, and the people reviewing your code build software for a living.",
      entries: [
        { title: "CSL BRANDS Mentoring Program", text: "Talent and community — the program today, and the physical environment we are building next.", state: "You are here" },
        { title: "CSL Studio", text: "Product, brand, and software execution — the real project context and working mentors behind the training." },
        { title: "CSL Labs", text: "A planned venture-building arm for the ideas and teams that outgrow the program." },
      ],
    },
    history: {
      marker: "04",
      eyebrow: "History",
      title: "The record so far.",
      lead: "Documented moments, not milestones invented after the fact. The full running log lives in our updates.",
      items: [
        { year: "2022", text: "CSL BRANDS founded in Douala." },
        { year: "Early 2025", text: "First cohort — nine mentees, completing 20+ structured courses in the early phase." },
        { year: "August 2025", text: "The first cohort closes its training phase after multiple sprint cycles on team projects." },
        { year: "November 2025", text: "Cohort 01 selected — ten mentees, with a public call for ten mentors." },
        { year: "February 2026", text: "Domain-Driven Design, Scrum, and Next.js; preparation for the next cohort begins." },
      ],
    },
    team: {
      marker: "05",
      eyebrow: "The team",
      title: "Real people, published properly.",
      lead: "Program team and mentor profiles are published here with each person's consent. Until then, this section stays honest: no stock faces, no invented bios.",
      note: "Want to know who you would be working with? Ask us directly — we respond to every message.",
      figures: [
        { label: "Media / Team / Mentor session" },
        { label: "Media / Team / Mentees at work" },
      ],
      cta: { label: "Contact the team", target: "contact" },
    },
    vision: {
      marker: "06",
      eyebrow: "The vision",
      kicker: "We are building",
      title: "The wider Space: a reliable home for talent and ventures in Douala.",
      lead: "The mentoring program is the operating foundation. The next phase is the physical environment around it — infrastructure that makes serious technology work dependable in Douala.",
      features: [
        { title: "Reliable power", text: "Dependable electricity, including solar — work that doesn't stop when the grid does." },
        { title: "High-speed internet", text: "Connectivity fit for distributed teams and remote collaboration." },
        { title: "Training rooms", text: "Dedicated space for cohorts, courses, and workshops." },
        { title: "Work areas", text: "Focused environments for teams building day to day." },
        { title: "Event capacity", text: "Room for demos, meetups, and the community that gathers around the work." },
        { title: "Startup space", text: "A place for product teams and early ventures to grow side by side." },
      ],
      note: "None of this is offered as an available facility today. We announce each part when it opens — not before.",
      cta: { label: "Join the work, or back it", target: "contact" },
    },
  },
};

export default en;
